import React, { useState, useEffect } from 'react';
import { Table, Button, Form, Modal } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';

interface Notification {
  id: number;
  message: string;
}

const NotificationList: React.FC = () => {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [newMessage, setNewMessage] = useState('');
  const [editNotification, setEditNotification] = useState<Notification | null>(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const navigate = useNavigate();
  const API_URL = process.env.REACT_APP_API_URL;

  useEffect(() => {
    const fetchNotifications = async () => {
      try {
        const token = localStorage.getItem('token');
        if (!token) {
          navigate('/login');
          return;
        }

        const res = await fetch(`${API_URL}/api/notifications`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
        if (!res.ok) throw new Error('Failed to fetch notifications');
        const data: Notification[] = await res.json();
        setNotifications(data);
      } catch (err) {
        console.error('Error fetching notifications:', err);
        alert(`Error fetching notifications: ${(err as Error).message || 'Network error'}`);
      }
    };
    fetchNotifications();
  }, [navigate]);

  const handleAddSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/notifications`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ message: newMessage }),
      });

      const data = await res.json();
      if (res.ok) {
        alert('Notification added successfully!');
        setNotifications([...notifications, { id: notifications.length + 1, message: newMessage }]);
        setNewMessage('');
        setShowAddModal(false);
      } else {
        alert(`Error adding notification: ${data.message || 'Unknown error'}`);
      }
    } catch (err) {
      console.error('Error adding notification:', err);
      alert(`Error adding notification: ${(err as Error).message || 'Network error'}`);
    }
  };

  const handleEdit = (notification: Notification) => {
    setEditNotification(notification);
    setNewMessage(notification.message);
    setShowEditModal(true);
  };

  const handleEditSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editNotification) return;

    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/notifications/${editNotification.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ message: newMessage }),
      });

      const data = await res.json();
      if (res.ok) {
        alert('Notification updated successfully!');
        setNotifications(
          notifications.map((notification) =>
            notification.id === editNotification.id ? { ...notification, message: newMessage } : notification
          )
        );
        setEditNotification(null);
        setNewMessage('');
        setShowEditModal(false);
      } else {
        alert(`Error updating notification: ${data.message || 'Unknown error'}`);
      }
    } catch (err) {
      console.error('Error updating notification:', err);
      alert(`Error updating notification: ${(err as Error).message || 'Network error'}`);
    }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('Are you sure you want to delete this notification?')) return;

    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/notifications/${id}`, {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const data = await res.json();
      if (res.ok) {
        alert('Notification deleted successfully!');
        setNotifications(notifications.filter((notification) => notification.id !== id));
      } else {
        alert(`Error deleting notification: ${data.message || 'Unknown error'}`);
      }
    } catch (err) {
      console.error('Error deleting notification:', err);
      alert(`Error deleting notification: ${(err as Error).message || 'Network error'}`);
    }
  };

  return (
    <>
      <h2 className="dashboard-title text-center mb-5">Manage Notifications</h2>
      <div className="text-end mb-3">
        <Button variant="primary" onClick={() => setShowAddModal(true)}>
          Add Notification
        </Button>
      </div>
      <Table striped bordered hover responsive className="modern-table">
        <thead>
          <tr>
            <th>ID</th>
            <th>Message</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {notifications.map((notification) => (
            <tr key={notification.id}>
              <td>{notification.id}</td>
              <td>{notification.message}</td>
              <td className="action-buttons">
                <Button
                  variant="warning"
                  size="sm"
                  className="me-2 modern-btn"
                  onClick={() => handleEdit(notification)}
                >
                  <i className="fas fa-edit"></i> Edit
                </Button>
                <Button
                  variant="danger"
                  size="sm"
                  className="modern-btn"
                  onClick={() => handleDelete(notification.id)}
                >
                  <i className="fas fa-trash-alt"></i> Delete
                </Button>
              </td>
            </tr>
          ))}
        </tbody>
      </Table>

      {/* Add Notification Modal */}
      <Modal show={showAddModal} onHide={() => setShowAddModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Add Notification</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form onSubmit={handleAddSubmit}>
            <Form.Group className="mb-3">
              <Form.Label>Message</Form.Label>
              <Form.Control
                type="text"
                value={newMessage}
                onChange={(e) => setNewMessage(e.target.value)}
                placeholder="Enter notification message"
                required
              />
            </Form.Group>
            <div className="text-center">
              <Button variant="primary" type="submit" className="me-2 modern-btn">
                <i className="fas fa-check"></i> Add
              </Button>
              <Button variant="secondary" className="modern-btn" onClick={() => setShowAddModal(false)}>
                <i className="fas fa-times"></i> Cancel
              </Button>
            </div>
          </Form>
        </Modal.Body>
      </Modal>

      {/* Edit Notification Modal */}
      {editNotification && (
        <Modal show={showEditModal} onHide={() => setShowEditModal(false)} centered>
          <Modal.Header closeButton>
            <Modal.Title>Edit Notification</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Form onSubmit={handleEditSubmit}>
              <Form.Group className="mb-3">
                <Form.Label>Message</Form.Label>
                <Form.Control
                  type="text"
                  value={newMessage}
                  onChange={(e) => setNewMessage(e.target.value)}
                  placeholder="Enter notification message"
                  required
                />
              </Form.Group>
              <div className="text-center">
                <Button variant="primary" type="submit" className="me-2 modern-btn">
                  <i className="fas fa-check"></i> Update
                </Button>
                <Button variant="secondary" className="modern-btn" onClick={() => setShowEditModal(false)}>
                  <i className="fas fa-times"></i> Cancel
                </Button>
              </div>
            </Form>
          </Modal.Body>
        </Modal>
      )}
    </>
  );
};

export default NotificationList;